import React, { useState, useEffect, useCallback } from "react";
import { Modal as RModal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useDispatch } from "react-redux";
import { startOfDay } from "date-fns";
import {
  loadRefreshPreview,
  refreshWorkPlans,
} from "../../Redux/Actions/ActionWorkPlans";
import { CheckFont } from "../General/GeneralComponent/CheckLang";
import "./CopyWorkPlanWizard.css";

const emptyAdditionalRow = () => ({
  product_id: "",
  batch_number: "",
  quantity: "",
});

function CopyWorkPlanWizard({
  duration,
  t,
  langmiamed,
  work_plan_id,
  open1,
  setOpen,
  setopenInternet,
  navigate,
}) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [topUps, setTopUps] = useState({});
  const [additionalLines, setAdditionalLines] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [submitLoad, setSubmitLoad] = useState(false);

  const resetWizard = useCallback(() => {
    setStep(1);
    setPreview(null);
    setTopUps({});
    setAdditionalLines([]);
    setStartDate("");
    setEndDate("");
    setDateError("");
    setSubmitLoad(false);
    setLoadingPreview(false);
  }, []);

  useEffect(() => {
    if (open1 && work_plan_id) {
      resetWizard();
      dispatch(
        loadRefreshPreview(
          work_plan_id,
          setPreview,
          setLoadingPreview,
          navigate,
          setopenInternet
        )
      );
    }
  }, [open1, work_plan_id, dispatch, navigate, setopenInternet, resetWizard]);

  useEffect(() => {
    if (startDate && duration) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(
        startDateObj.getTime() + duration * 24 * 60 * 60 * 1000
      );
      const formattedEndDate = endDateObj.toISOString().slice(0, 10);
      setEndDate(formattedEndDate);
    }
  }, [startDate, duration]);

  const handleClose = () => {
    setOpen(false);
    resetWizard();
  };

  const handleTopUpChange = (lineId, value) => {
    if (lineId == null || lineId === "") return;
    const n = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setTopUps((prev) => ({ ...prev, [lineId]: n }));
  };

  const addAdditionalRow = () => {
    setAdditionalLines((prev) => [...prev, emptyAdditionalRow()]);
  };

  const updateAdditionalRow = (index, field, value) => {
    setAdditionalLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeAdditionalRow = (index) => {
    setAdditionalLines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStartDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const today = startOfDay(new Date());
    if (selectedDate <= today) {
      setDateError(`${t("errRefresh")}`);
    } else {
      setDateError("");
      setStartDate(e.target.value);
    }
  };

  const buildLineTopUps = () => {
    return Object.entries(topUps)
      .filter(([, qty]) => qty !== "" && Number(qty) > 0)
      .filter(([id]) => /^\d+$/.test(String(id)))
      .map(([id, qty]) => ({
        source_work_plan_product_id: parseInt(id, 10),
        quantity: Number(qty),
      }));
  };

  /** Top-ups on cumulative-only rows (no source line id) become additional_lines */
  const buildOrphanTopUpsAsAdditional = () => {
    const allRows = [
      ...(preview?.product_lines || []),
      ...(preview?.marketing_lines || []),
    ];
    const extras = [];
    Object.entries(topUps).forEach(([id, qty]) => {
      if (qty === "" || Number(qty) <= 0) return;
      if (/^\d+$/.test(String(id))) return;
      const row = allRows.find(
        (r) =>
          `${r.product_id}|${r.batch_number ?? ""}` === id ||
          `m-${r.product_id}` === id
      );
      if (!row) return;
      extras.push({
        product_id: parseInt(row.product_id, 10),
        batch_number: row.batch_number ? String(row.batch_number) : "",
        quantity: Number(qty),
      });
    });
    return extras;
  };

  const buildAdditionalLinesPayload = () => {
    const manual = additionalLines
      .filter((r) => r.product_id && r.quantity)
      .map((r) => ({
        product_id: parseInt(r.product_id, 10),
        batch_number: r.batch_number ? String(r.batch_number) : "",
        quantity: parseInt(r.quantity, 10),
      }));
    return [...manual, ...buildOrphanTopUpsAsAdditional()];
  };

  const submit = (e) => {
    e.preventDefault();
    if (!startDate || dateError) {
      return;
    }
    dispatch(
      refreshWorkPlans(
        work_plan_id,
        startDate,
        endDate,
        duration,
        navigate,
        setopenInternet,
        setSubmitLoad,
        buildAdditionalLinesPayload(),
        buildLineTopUps(),
        handleClose
      )
    );
  };

  const productLines = preview?.product_lines || [];
  const marketingLines = preview?.marketing_lines || [];

  const renderInventoryTable = (rows, isMarketing) => {
    if (!rows.length) {
      return (
        <p className="copyWizardMuted" style={{ fontFamily: CheckFont(t("copyWizard_noLines")) }}>
          {t("copyWizard_noLines")}
        </p>
      );
    }
    return (
      <div className="copyWizardTableWrap">
        <table className="copyWizardTable">
          <thead>
            <tr>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_product")) }}>
                {isMarketing ? t("copyWizard_col_tool") : t("copyWizard_col_product")}
              </th>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_batch")) }}>
                {t("copyWizard_col_batch")}
              </th>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_planned")) }}>
                {t("copyWizard_col_planned")}
              </th>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_delivered")) }}>
                {t("copyWizard_col_delivered")}
              </th>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_remaining")) }}>
                {t("copyWizard_col_remaining")}
              </th>
              <th style={{ fontFamily: CheckFont(t("copyWizard_col_topup")) }}>
                {t("copyWizard_col_topup")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id =
                row.work_plan_product_id != null
                  ? row.work_plan_product_id
                  : isMarketing
                    ? `m-${row.product_id}`
                    : `${row.product_id}|${row.batch_number ?? ""}`;
              const direct = row.quantity_delivered_direct ?? 0;
              const legacy = row.quantity_delivered_legacy_allocated ?? 0;
              const deliveredTotal = direct + legacy;
              const remaining = row.quantity_remaining ?? 0;
              const zeroClass = remaining === 0 ? "copyWizardRowZero" : "";
              const name = isMarketing
                ? row.tool_name || row.marketing_tool_id
                : row.product_name || row.product_id;
              const batchDisplay = isMarketing ? "—" : row.batch_number || "—";
              return (
                <tr key={String(id)} className={zeroClass}>
                  <td style={{ fontFamily: CheckFont(String(name)) }}>
                    {name}
                    {row.in_source_plan === false && (
                      <span className="copyWizardBadge" title={t("copyWizard_from_other_plans")}>
                        {" "}
                        ({t("copyWizard_from_other_plans")})
                      </span>
                    )}
                  </td>
                  <td style={{ fontFamily: CheckFont(String(batchDisplay)) }}>
                    {batchDisplay}
                  </td>
                  <td>{row.quantity_planned}</td>
                  <td>{deliveredTotal}</td>
                  <td>
                    <strong>{remaining}</strong>
                    {row.warnings && row.warnings.length > 0 && (
                      <span className="copyWizardWarn" title={row.warnings.join(", ")}>
                        {" "}
                        !
                      </span>
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="copyWizardTopUpInput"
                      value={topUps[id] === undefined || topUps[id] === "" ? "" : topUps[id]}
                      onChange={(e) => handleTopUpChange(id, e.target.value)}
                      placeholder="0"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <RModal
      open={open1}
      onClose={handleClose}
      center={true}
      classNames={{
        modal: "popAdd copyWizardModal",
      }}
      focusTrapped={false}
    >
      <div
        className="popAddForm copyWizardForm"
        dir={langmiamed === "ar" ? "rtl" : "ltr"}
      >
        <h3
          className="copyWizardTitle"
          style={{ fontFamily: CheckFont(t("copyWizard_title")) }}
        >
          {t("copyWizard_title")}
        </h3>
        <p className="copyWizardStepLabel" style={{ fontFamily: CheckFont(t(`copyWizard_step${step}`)) }}>
          {t(`copyWizard_step${step}`)}
        </p>
        {preview?.balance_mode === "cumulative" && (
          <p className="copyWizardCumulativeHint" style={{ fontFamily: CheckFont(t("copyWizard_cumulative_hint")) }}>
            {t("copyWizard_cumulative_hint")}
          </p>
        )}

        {step === 1 && (
          <>
            {loadingPreview && (
              <p style={{ fontFamily: CheckFont(t("copyWizard_loading")) }}>
                {t("copyWizard_loading")}
              </p>
            )}
            {!loadingPreview && preview && (
              <>
                <h4
                  className="copyWizardSection"
                  style={{ fontFamily: CheckFont(t("copyWizard_products")) }}
                >
                  {t("copyWizard_products")}
                </h4>
                {renderInventoryTable(productLines, false)}
                <h4
                  className="copyWizardSection"
                  style={{ fontFamily: CheckFont(t("copyWizard_marketing")) }}
                >
                  {t("copyWizard_marketing")}
                </h4>
                {renderInventoryTable(marketingLines, true)}
                <h4
                  className="copyWizardSection"
                  style={{ fontFamily: CheckFont(t("copyWizard_newLines")) }}
                >
                  {t("copyWizard_newLines")}
                </h4>
                <p className="copyWizardMuted" style={{ fontFamily: CheckFont(t("copyWizard_newLines_hint")) }}>
                  {t("copyWizard_newLines_hint")}
                </p>
                {additionalLines.map((row, index) => (
                  <div key={index} className="copyWizardAdditionalRow">
                    <input
                      type="number"
                      placeholder={t("copyWizard_product_id_ph")}
                      value={row.product_id}
                      onChange={(e) =>
                        updateAdditionalRow(index, "product_id", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      placeholder={t("copyWizard_batch_ph")}
                      value={row.batch_number}
                      onChange={(e) =>
                        updateAdditionalRow(index, "batch_number", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      min="1"
                      placeholder={t("copyWizard_qty_ph")}
                      value={row.quantity}
                      onChange={(e) =>
                        updateAdditionalRow(index, "quantity", e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="copyWizardRemoveBtn"
                      onClick={() => removeAdditionalRow(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="copyWizardAddBtn"
                  onClick={addAdditionalRow}
                  style={{ fontFamily: CheckFont(t("copyWizard_add_line")) }}
                >
                  {t("copyWizard_add_line")}
                </button>
              </>
            )}
            <div className="div_btn copyWizardNav">
              <button
                type="button"
                onClick={handleClose}
                style={{ fontFamily: CheckFont(t("cancel")) }}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                disabled={loadingPreview || !preview}
                onClick={() => setStep(2)}
                style={{ fontFamily: CheckFont(t("copyWizard_next")) }}
              >
                {t("copyWizard_next")}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <form onSubmit={submit}>
            <div className="one_input_row" style={{ width: "100%" }}>
              <div className="Gen_icon">
                <i className="las la-calendar"></i>
                <span
                  style={{ fontFamily: CheckFont(t("startDate")) }}
                  className={langmiamed === "ar" ? "textAlignRight" : ""}
                >
                  {t("startDate")}
                </span>
              </div>
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
              />
              {dateError && (
                <span
                  className={`color_redd ${
                    langmiamed === "ar" ? "textAlignRight" : "margin_left_10"
                  }`}
                >
                  {dateError}
                </span>
              )}
            </div>
            <br />
            <div className="one_input_row" style={{ width: "100%" }}>
              <div className="Gen_icon">
                <i className="las la-calendar"></i>
                <span
                  style={{ fontFamily: CheckFont(t("endDate")) }}
                  className={langmiamed === "ar" ? "textAlignRight" : ""}
                >
                  {t("endDate")}
                </span>
              </div>
              <input type="date" value={endDate} disabled readOnly />
            </div>
            <br />
            <div className="one_input_row">
              <div className="Gen_icon">
                <i className="las la-sort-numeric-up-alt"></i>
                <span
                  style={{ fontFamily: CheckFont(t("dayDuration")) }}
                  className={langmiamed === "ar" ? "textAlignRight" : ""}
                >
                  {t("dayDuration")}
                </span>
              </div>
              <input value={duration} type="number" disabled readOnly />
            </div>
            <div className="div_btn copyWizardNav">
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{ fontFamily: CheckFont(t("copyWizard_back")) }}
              >
                {t("copyWizard_back")}
              </button>
              <button
                type="submit"
                style={{ fontFamily: CheckFont(t("refresh")) }}
              >
                {submitLoad ? "Loading ..." : `${t("refresh")}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </RModal>
  );
}

export default CopyWorkPlanWizard;
